import { BooleanFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { CompanyItem, isOk } from "@/common/common";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { DataTableProps } from "@/components-v2/DataTable";
import { navigateToAggregatedCompanyRoleDetails } from "./aggregatedContactDetailsDomain";
import { SelectedPortfolioOption } from "@/portfolio/portfolioDomain";
import { EventDispatcher, navigateTo } from "@/events/eventsDomain";
import { DropdownMenuItemType } from "@/components-v2/Dropdown";
import { getFullName } from "@/utils/Roles";
import { isBlank } from "@/utils/strings";
import { AggregatedContactItem, ContactsState, RoleItem } from "./contactsDomain";

import { TextWithTooltipProps } from "@/components-v2/TextWithTooltip";
import { formatDate } from "@/i18n/dateFormat.ts";
import { Language } from "@/i18n/languages.ts";
import { CompanyRole } from "@/types/CompanyInfo.ts";
import { TMessageKey } from "@/i18n/types.gen.ts";

export type CompanyRolesViewProps = {
  aggregatedPersonItems: AggregatedContactItem[];
  roleNameFilter?: StringFieldModel;
  selectedCompany?: RadioFieldModel<CompanyItem | null>;
  selectedRoleType?: RadioFieldModel<RoleType | null>;
  navigateToDetails: (id: string | number) => void;
  filterMissingEmailModel?: BooleanFieldModel;
  filterMissingEmailLabel?: TMessage;
  isFilterMissingEmailDisabled?: boolean;
  rowActions: DataTableProps<AggregatedContactItem>["rowActions"];
};

export const prepCompanyRolesView = ({
  state,
  dispatch,
  portfolio,
}: {
  state: ContactsState;
  dispatch: EventDispatcher;
  portfolio: SelectedPortfolioOption;
}): CompanyRolesViewProps => {
  const { response } = state;
  const aggregatedPersonItems = isOk(response)
    ? response.data.contacts.filter((item) => item.roleItems.length > 0)
    : [];
  const missingEmail = countMissingCompanyRoleEmails(aggregatedPersonItems);

  return {
    aggregatedPersonItems: aggregatedPersonItems,
    roleNameFilter: state.roleNameFilter,
    selectedCompany: state.selectedCompany,
    selectedRoleType: state.selectedRoleType,
    navigateToDetails: (companyroleId) =>
      dispatch(
        navigateToAggregatedCompanyRoleDetails({
          id: companyroleId,
          portfolio,
        }),
      ),
    filterMissingEmailModel: state.filterMissingEmailModel,
    filterMissingEmailLabel: {
      key: "contacts.missing-email",
      params: {
        count: missingEmail,
      },
    },
    isFilterMissingEmailDisabled: missingEmail === 0,
    rowActions: (row) => {
      return [
        [
          "context-menu",
          [
            {
              type: "item",
              label: { key: "contacts.company-roles.view-details" },
              icon: "user-circle",
              onSelect: () =>
                dispatch(
                  navigateToAggregatedCompanyRoleDetails({
                    id: row.aggregatedContactId,
                    portfolio,
                  }),
                ),
            },
            { type: "label", label: { key: "contacts.company-roles-go-to-company" } },
            ...row.roleItems.map<DropdownMenuItemType>((c) => ({
              type: "item",
              label: c.company.companyName,
              icon: "eye",
              onSelect: () =>
                dispatch([
                  navigateTo({
                    name: "admin/roles",
                    params: { orgNo: c.company.organisationNumber },
                  }),
                ]),
            })),
          ],
        ],
      ];
    },
  };
};

export const filterCompanyRoleRows = (
  selectedCompany?: CompanyItem | null,
  selectedRoleType?: RoleType | null,
  roleItems?: AggregatedContactItem[],
  filterInputString?: string,
  missingEmailOnly?: boolean,
): AggregatedContactItem[] => {
  const filterInput = filterInputString?.toLowerCase().trim();
  const noText = !filterInput || filterInput.length === 0;
  const noCompany = !selectedCompany;
  const noRoleType = !selectedRoleType;
  if (noText && noCompany && noRoleType && !missingEmailOnly) return roleItems ?? [];

  return (roleItems ?? []).filter((agg) =>
    matchesRoleItem(agg, selectedCompany, selectedRoleType, filterInput, missingEmailOnly),
  );
};

const matchesRoleItem = (
  aggregatedRoleItem: AggregatedContactItem,
  selectedCompany?: CompanyItem | null,
  selectedRoleType?: RoleType | null,
  filterInput?: string,
  missingEmailOnly?: boolean,
): boolean => {
  const textMatches =
    !filterInput ||
    aggregatedRoleItem.roleItems.some(
      (i) =>
        getFullName(i.role).toLowerCase().includes(filterInput) ||
        i.company.companyName.toLowerCase().includes(filterInput) ||
        i.role.role.toLowerCase().includes(filterInput),
    );
  const companyMatches =
    !selectedCompany ||
    aggregatedRoleItem.roleItems.some(
      (role) => role.company?.organisationNumber === selectedCompany.organisationNumber,
    );
  const roleTypeMatches =
    !selectedRoleType ||
    aggregatedRoleItem.roleItems.some((item) => item.role.role === selectedRoleType);

  const matchesEmail = !missingEmailOnly || countMissingCompanyRoleEmails([aggregatedRoleItem]) > 0;
  return textMatches && companyMatches && roleTypeMatches && matchesEmail;
};

export const companyRolesRoleType = (items: RoleItem[]): TextWithTooltipProps => {
  const countsMap = new Map<RoleType, number>();
  for (const item of items) {
    countsMap.set(item.role.role, (countsMap.get(item.role.role) || 0) + 1);
  }

  const groupedWithCounts = Array.from(countsMap.entries()).map(([roleType, count]) => ({
    roleType,
    count,
  }));

  if (groupedWithCounts.length === 1) {
    return {
      text: getRoleTypeLabel(groupedWithCounts[0].roleType),
    };
  }
  return {
    text: { key: "contacts.multiple" },
    tooltip: {
      type: "md-list",
      value: groupedWithCounts.map(({ roleType, count }) => ({
        key: "contacts.company-roles.x-with-count",
        params: {
          x: getRoleTypeLabel(roleType),
          count,
        },
      })),
    },
  };
};

export function companyRolesCompanies(roleItems: RoleItem[]): TextWithTooltipProps {
  const companyItems = roleItems.map((c) => c.company);
  if (companyItems.length === 1) {
    return { text: { translated: companyItems[0].companyName } };
  }
  return {
    tooltip: { type: "md-list", value: companyItems.map((c) => c.companyName) },
    text: { key: "portfolio.numberOfCompanies", params: { count: companyItems.length } },
  };
}

export function companyRolesValidFrom(
  roleItems: RoleItem[],
  language: Language,
): TextWithTooltipProps {
  const validFroms = roleItems.map((i) => i.role.validFrom);
  const notSet = validFroms.filter(isBlank);
  if (notSet.length === roleItems.length) return { text: { key: "not-set" }, hasNotSet: true };
  const tooltip: TMessageParam = {
    type: "md-list",
    value: roleItems.map((i, index) => ({
      type: "list",
      separator: " - ",
      value: [
        i.company.companyName,
        getRoleTypeLabel(i.role.role),
        isBlank(validFroms[index])
          ? { key: "not-set" }
          : { type: "date", value: validFroms[index] },
      ],
    })),
  };
  if (notSet.length > 0) return { tooltip, text: { key: "contacts.incomplete" } };
  if (roleItems.length === 1) return { text: { translated: formatDate(language, validFroms[0]) } };
  return { tooltip, text: { key: "contacts.multiple" } };
}

export const companyRolesNationality = (roleItems: RoleItem[]): TextWithTooltipProps =>
  companyRolesCountry(
    roleItems,
    (role) => role.nationality,
    "contacts.company-roles.nationality-missing",
  );

export const companyRolesCountryOfResidence = (roleItems: RoleItem[]): TextWithTooltipProps =>
  companyRolesCountry(
    roleItems,
    (role) => role.residentialAddress?.country,
    "contacts.company-roles.residence-missing",
  );

function companyRolesCountry(
  roleItems: RoleItem[],
  getCountry: (role: CompanyRole) => string | undefined,
  missingKey: TMessageKey,
): TextWithTooltipProps {
  const countries = roleItems.map((i) => getCountry(i.role));
  const notSet = countries.filter(isBlank);

  if (notSet.length === roleItems.length)
    return { text: { key: "not-set" }, hasNotSet: true, tooltip: { key: missingKey } };
  const tooltip: TMessageParam = {
    type: "md-list",
    value: roleItems.map((i, index) => ({
      type: "list",
      separator: " - ",
      value: [
        i.company.companyName,
        getRoleTypeLabel(i.role.role),
        countries[index] || { key: "not-set" },
      ],
    })),
  };
  if (notSet.length > 0) return { tooltip, text: { key: "contacts.incomplete" } };
  if (roleItems.length === 1) return { text: { translated: countries[0] ?? "" } };
  return { tooltip, text: { key: "contacts.multiple" } };
}

export function companyRolesEmail(roleItems: RoleItem[]): TextWithTooltipProps {
  const emails = roleItems.map((e) => e.role.email).filter((email) => email !== undefined);
  const areAllEmailsSame = emails.length > 0 && emails.every((email) => email === emails[0]);
  const rolesEmailNotSet = roleItems.filter((f) => isBlank(f.role.email));
  if (rolesEmailNotSet.length === roleItems.length) {
    return {
      tooltip: {
        key: "contacts.email-not-set",
        params: {
          count: rolesEmailNotSet.length,
        },
      },
      text: { key: "not-set" },
    };
  } else if (areAllEmailsSame) {
    return { text: { translated: emails[0] } };
  } else if (rolesEmailNotSet.length > 0) {
    return {
      tooltip: {
        key: "contacts.incomplete-email",
        params: {
          count: rolesEmailNotSet.length,
          name: rolesEmailNotSet.length === 1 ? rolesEmailNotSet[0].company.companyName : undefined,
        },
      },
      text: { key: "contacts.incomplete" },
    };
  }
  return {
    tooltip: { key: "contacts.multiple-emails-found" },
    text: { key: "contacts.multiple" },
  };
}

const countMissingCompanyRoleEmails = (aggregatedRoleItems: AggregatedContactItem[]): number =>
  aggregatedRoleItems.reduce(
    (acc, x) => acc + x.roleItems.filter((i) => isBlank(i.role.email)).length,
    0,
  );
